import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import type { SubscriptionTier } from "@/lib/subscription";

interface PlanLimitsRow {
  tier: SubscriptionTier;
  project_limit: number;
  variation_limit: number;
}

/**
 * Reads the `plan_limits` row for the current user's subscription tier.
 * DB is the source of truth for enforcement; this exposes the same numbers to the UI.
 */
export function usePlanLimits() {
  const { tier, isLoading: tierLoading } = useSubscription();

  const { data, isLoading } = useQuery({
    queryKey: ["plan_limits", tier],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plan_limits")
        .select("tier, project_limit, variation_limit")
        .eq("tier", tier)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as PlanLimitsRow | null;
    },
    enabled: !tierLoading,
    staleTime: 5 * 60 * 1000,
  });

  return {
    tier,
    projectLimit: data?.project_limit ?? null,
    variationLimit: data?.variation_limit ?? null,
    isLoading: tierLoading || isLoading,
  };
}
